import CryptoJS from 'crypto-js';
import * as turf from '@turf/turf';
import tilebelt from '@mapbox/tilebelt';

export const generatePolygonId = (polygon, decimals = 6) => {
  // Normalize the polygon coordinates
  const normalizedPolygon = polygon.map(([x, y]) => [
    parseFloat(x.toFixed(decimals)),
    parseFloat(y.toFixed(decimals))
  ]);

  // Convert the polygon coordinates to a JSON string
  const polygonStr = JSON.stringify(normalizedPolygon);

  // Create a unique hash using MD5
  const hash = CryptoJS.MD5(polygonStr).toString();

  return hash;
};

export const calculatePolygonAreaHa = coords => {
  const R = 6371.0; // Earth radius in kilometers

  // Convert coordinates from degrees to radians
  const coordsRad = coords.map(([lng, lat]) => [
    (lng * Math.PI) / 180,
    (lat * Math.PI) / 180
  ]);

  // Use the spherical excess formula to calculate the area
  let totalArea = 0.0;
  for (let i = 0; i < coordsRad.length; i++) {
    const [lng1, lat1] = coordsRad[i];
    const [lng2, lat2] = coordsRad[(i + 1) % coordsRad.length];

    // Calculate the spherical excess for the edge
    totalArea += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }

  totalArea = Math.abs(totalArea) / 2.0;

  // Convert the area from square radians to square kilometers and then to hectares
  const areaHa = R ** 2 * totalArea * 100; // 1 km² = 100 ha
  return areaHa;
};

export const getIntersectingTiles = (polygons, zoom) => {
  const tileSet = new Set();

  polygons.forEach(coords => {
    const polygon = turf.polygon([coords]);

    // Get bounding box of the polygon
    const bbox = turf.bbox(polygon);
    // Determine the range of tiles that need to be checked
    const minTile = tilebelt.pointToTile(bbox[0], bbox[1], zoom);
    const maxTile = tilebelt.pointToTile(bbox[2], bbox[3], zoom);

    // Determine the actual min and max for x and y
    const xMin = Math.min(minTile[0], maxTile[0]);
    const xMax = Math.max(minTile[0], maxTile[0]);
    const yMin = Math.min(minTile[1], maxTile[1]);
    const yMax = Math.max(minTile[1], maxTile[1]);

    // Loop through each tile in the bounding box range
    for (let x = xMin; x <= xMax; x++) {
      for (let y = yMin; y <= yMax; y++) {
        // Convert tile to a bounding box and then to a polygon
        const tileBbox = tilebelt.tileToBBOX([x, y, zoom]);
        const tilePolygon = turf.bboxPolygon(tileBbox);
        // Check if the polygon intersects with the tile polygon
        if (turf.booleanIntersects(polygon, tilePolygon)) {
          // Add tile to the set if it intersects
          tileSet.add(JSON.stringify([zoom, x, y]));
        }
      }
    }
  });

  // Convert set of JSON strings back to array of tuples
  const result = Array.from(tileSet).map(tile => JSON.parse(tile));
  return result;
};
